import { useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Chip, Box,
  Accordion, AccordionSummary, AccordionDetails, Typography, Alert, CircularProgress, Divider,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createRole, updateRole, getRole, listPermissions, previewRoleImpact,
  type RoleListItem, type PermissionModuleGroup,
} from '../../api/admin'
import { errorMessage } from '../../api/client'

const schema = z.object({
  code: z.string().min(2, 'Minimum 2 caractere').max(64).regex(/^[A-Z0-9_]+$/i, 'Doar litere, cifre și underscore'),
  name: z.string().min(1, 'Câmp obligatoriu'),
  description: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

export default function RoleFormDialog({ role, onClose }: { role: RoleListItem | null | 'new'; onClose: () => void }) {
  const open = role !== null
  const editing = role !== null && role !== 'new'
  const qc = useQueryClient()

  const [permissionIds, setPermissionIds] = useState<Set<string>>(new Set())

  const { data: catalog } = useQuery({ queryKey: ['admin', 'permissions'], queryFn: listPermissions, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['admin', 'role', editing ? (role as RoleListItem).id : null],
    queryFn: () => getRole((role as RoleListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', name: '', description: '' },
  })

  useEffect(() => {
    if (editing && detail) {
      reset({ code: detail.code, name: detail.name, description: detail.description ?? '' })
      setPermissionIds(new Set(detail.permissionIds))
    } else if (role === 'new') {
      reset({ code: '', name: '', description: '' })
      setPermissionIds(new Set())
    }
  }, [role, editing, detail, reset])

  // Previzualizare de impact — debounced, doar în editare (are nevoie de un id existent).
  const [debouncedIds, setDebouncedIds] = useState<string[]>([])
  useEffect(() => {
    const t = setTimeout(() => setDebouncedIds(Array.from(permissionIds)), 400)
    return () => clearTimeout(t)
  }, [permissionIds])

  const { data: impact } = useQuery({
    queryKey: ['admin', 'role-impact', editing ? (role as RoleListItem).id : null, debouncedIds],
    queryFn: () => previewRoleImpact((role as RoleListItem).id, debouncedIds),
    enabled: editing && !!detail,
  })

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const ids = Array.from(permissionIds)
      if (editing) {
        const current = role as RoleListItem
        return updateRole(current.id, { name: values.name, description: values.description || null, permissionIds: ids })
      }
      return createRole({ code: values.code, name: values.name, description: values.description || null, permissionIds: ids })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'roles'] })
      onClose()
    },
  })

  const toggle = (id: string) => setPermissionIds((s) => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const toggleResource = (ids: string[], selectAll: boolean) => setPermissionIds((s) => {
    const next = new Set(s)
    ids.forEach((id) => (selectAll ? next.add(id) : next.delete(id)))
    return next
  })

  const isSystemRole = editing && detail?.isSystem

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare rol' : 'Rol nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {isSystemRole && (
              <Alert severity="warning" variant="outlined">
                Rol de sistem — codul și permisiunile nu pot fi modificate din interfață.
              </Alert>
            )}
            <Stack direction="row" spacing={2}>
              <Controller name="code" control={control} render={({ field }) => (
                <TextField {...field} label="Cod" disabled={editing} sx={{ width: 220 }}
                  error={!!errors.code} helperText={errors.code?.message} />
              )} />
              <Controller name="name" control={control} render={({ field }) => (
                <TextField {...field} label="Nume" fullWidth disabled={!!isSystemRole}
                  error={!!errors.name} helperText={errors.name?.message} />
              )} />
            </Stack>
            <Controller name="description" control={control} render={({ field }) => (
              <TextField {...field} label="Descriere" fullWidth multiline minRows={2} disabled={!!isSystemRole} />
            )} />

            {impact && (impact.permissionsGained.length > 0 || impact.permissionsLost.length > 0) && (
              <Alert severity="info" variant="outlined">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Impact: {impact.affectedUserCount} utilizator(i) activ(i) afectat(ți)
                </Typography>
                {impact.permissionsGained.length > 0 && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                    +{impact.permissionsGained.length} permisiuni câștigate
                  </Typography>
                )}
                {impact.permissionsLost.length > 0 && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                    −{impact.permissionsLost.length} permisiuni pierdute
                  </Typography>
                )}
              </Alert>
            )}

            <Divider><Typography variant="overline" color="text.secondary">Permisiuni ({permissionIds.size})</Typography></Divider>

            <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {catalog?.map((mod) => (
                <ModuleAccordion key={mod.module} mod={mod} selected={permissionIds}
                  disabled={!!isSystemRole} onToggle={toggle} onToggleResource={toggleResource} />
              ))}
            </Box>

            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          {!isSystemRole && (
            <Button type="submit" variant="contained" disabled={mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
              Salvează
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}

function ModuleAccordion({ mod, selected, disabled, onToggle, onToggleResource }: {
  mod: PermissionModuleGroup; selected: Set<string>; disabled: boolean
  onToggle: (id: string) => void; onToggleResource: (ids: string[], selectAll: boolean) => void
}) {
  const allIds = useMemo(() => mod.resources.flatMap((r) => r.permissions.map((p) => p.id)), [mod])
  const selectedCount = allIds.filter((id) => selected.has(id)).length

  return (
    <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' }, mb: 0.75 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
          {mod.module} <Typography component="span" variant="caption" color="text.secondary">({selectedCount}/{allIds.length})</Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.25}>
          {mod.resources.map((res) => {
            const resIds = res.permissions.map((p) => p.id)
            const allSelected = resIds.every((id) => selected.has(id))
            return (
              <Box key={res.resource}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{res.resource}</Typography>
                  {!disabled && (
                    <Chip
                      label={allSelected ? 'deselectează tot' : 'selectează tot'} size="small" variant="outlined"
                      onClick={() => onToggleResource(resIds, !allSelected)}
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {res.permissions.map((p) => (
                    <Chip
                      key={p.id} label={p.action} size="small"
                      color={selected.has(p.id) ? 'primary' : 'default'}
                      variant={selected.has(p.id) ? 'filled' : 'outlined'}
                      onClick={disabled ? undefined : () => onToggle(p.id)}
                    />
                  ))}
                </Box>
              </Box>
            )
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
